import React, { useState } from 'react';
import axios from 'axios';
import { describeError } from '../../components/ErrorToast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = 'w-full border border-slate-300 rounded px-2 py-1 text-[11px] bg-white';
const lbl = 'block text-[10px] uppercase font-bold text-slate-600 mb-0.5';

export const StepCard = ({ n, title, hint, testid, children }) => (
  <div className="mb-5 border border-slate-200 rounded-lg overflow-hidden" data-testid={testid}>
    <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 border-b border-slate-200">
      <span className="w-5 h-5 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center">{n}</span>
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">{title}</span>
      {hint && <span className="ml-auto text-[10px] italic text-slate-400">{hint}</span>}
    </div>
    <div className="p-3 text-xs">{children}</div>
  </div>
);

const ReportView = ({ r }) => (
  <div className="space-y-1.5" data-testid="vendor-report-view">
    <div className="flex flex-wrap gap-x-4 text-[10.5px] text-slate-600">
      {r.vendor_name && <span>Vendor: <b className="text-slate-800">{r.vendor_name}</b></span>}
      {r.vendor_ref_no && <span>Ref: <b className="font-mono text-slate-800">{r.vendor_ref_no}</b></span>}
      <span>Reported: {r.reported_on}</span>
      {r.recorded_by_name && <span>Logged by {r.recorded_by_name}</span>}
    </div>
    <div className="whitespace-pre-wrap text-slate-800" data-testid="vendor-report-diagnosis">{r.diagnosis}</div>
    {r.items?.length > 0 && (
      <ul className="border-t border-slate-100 pt-1 space-y-0.5" data-testid="vendor-report-items">
        {r.items.map((it, i) => (
          <li key={i} className="flex gap-2"><span className={`px-1.5 rounded text-[9.5px] font-bold uppercase ${it.action === 'replace' ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'}`}>{it.action}</span>
            <span className="flex-1">{it.description}</span><span className="text-slate-500">×{it.qty}</span></li>
        ))}
      </ul>
    )}
  </div>
);

export function VendorReportStep({ ticket, canEdit, onSaved }) {
  const r = ticket.vendor_report;
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ diagnosis: r?.diagnosis || '', vendor_name: r?.vendor_name || '', vendor_ref_no: r?.vendor_ref_no || '', reported_on: r?.reported_on || '' });
  const [items, setItems] = useState(r?.items?.length ? r.items : [{ description: '', action: 'repair', qty: 1 }]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const setItem = (i, k, v) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await axios.put(`${API}/ha/service-tickets/${ticket.ticket_no}/vendor-report`, {
        ...f, reported_on: f.reported_on || undefined,
        items: items.filter((x) => x.description.trim()).map((x) => ({ ...x, qty: Number(x.qty) || 1 })),
      });
      setEditing(false); onSaved();
    } catch (e) { setErr(describeError(e, 'Could not save vendor report')); } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <StepCard n="A" title="Vendor service report" testid="step-vendor-report"
        hint={canEdit ? <button onClick={() => setEditing(true)} data-testid="vendor-report-edit" className="not-italic font-semibold text-indigo-600 hover:underline">{r ? 'Edit' : '+ Record report'}</button> : null}>
        {r ? <ReportView r={r} /> : <div className="italic text-slate-400 text-center" data-testid="vendor-report-empty">No vendor report yet.</div>}
      </StepCard>
    );
  }
  return (
    <StepCard n="A" title="Vendor service report" testid="step-vendor-report">
      <div className="space-y-2" data-testid="vendor-report-form">
        <div className="grid grid-cols-3 gap-2">
          <div><label className={lbl}>Vendor name</label><input value={f.vendor_name} onChange={(e) => setF({ ...f, vendor_name: e.target.value })} data-testid="vendor-report-vendor-name" className={inp} /></div>
          <div><label className={lbl}>Vendor ref no.</label><input value={f.vendor_ref_no} onChange={(e) => setF({ ...f, vendor_ref_no: e.target.value })} data-testid="vendor-report-ref" className={inp} /></div>
          <div><label className={lbl}>Report date</label><input type="date" value={f.reported_on} onChange={(e) => setF({ ...f, reported_on: e.target.value })} data-testid="vendor-report-date" className={inp} /></div>
        </div>
        <div><label className={lbl}>Diagnosis *</label><textarea rows={3} value={f.diagnosis} onChange={(e) => setF({ ...f, diagnosis: e.target.value })} data-testid="vendor-report-diagnosis-input" placeholder="What the service centre found" className={inp} /></div>
        <div>
          <label className={lbl}>Items to repair / replace</label>
          {items.map((it, i) => (
            <div key={i} className="flex gap-1.5 mb-1" data-testid={`vendor-report-item-${i}`}>
              <input value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} placeholder="e.g. Receiver, battery door" data-testid={`vendor-report-item-desc-${i}`} className={`${inp} flex-1`} />
              <select value={it.action} onChange={(e) => setItem(i, 'action', e.target.value)} data-testid={`vendor-report-item-action-${i}`} className="border border-slate-300 rounded px-1 text-[11px] bg-white">
                <option value="repair">Repair</option><option value="replace">Replace</option>
              </select>
              <input type="number" min={1} value={it.qty} onChange={(e) => setItem(i, 'qty', e.target.value)} data-testid={`vendor-report-item-qty-${i}`} className="w-12 border border-slate-300 rounded px-1 text-[11px]" />
              <button onClick={() => setItems((xs) => xs.filter((_, j) => j !== i))} aria-label="Remove item" data-testid={`vendor-report-item-remove-${i}`} className="px-1.5 text-rose-600">×</button>
            </div>
          ))}
          <button onClick={() => setItems((xs) => [...xs, { description: '', action: 'repair', qty: 1 }])} data-testid="vendor-report-add-item" className="text-[11px] font-semibold text-indigo-600 hover:underline">+ Add item</button>
        </div>
        {err && <div className="text-[11px] text-rose-700" data-testid="vendor-report-error">{err}</div>}
        <div className="flex gap-2 justify-end">
          <button onClick={() => setEditing(false)} data-testid="vendor-report-cancel" className="px-3 py-1 rounded border border-slate-300 text-[11px]">Cancel</button>
          <button onClick={save} disabled={busy || f.diagnosis.trim().length < 2} data-testid="vendor-report-save" className="px-3 py-1 rounded bg-indigo-600 text-white text-[11px] font-bold disabled:opacity-50">{busy ? 'Saving…' : 'Save report'}</button>
        </div>
      </div>
    </StepCard>
  );
}
